import { useCallback, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { OrganisationMembership } from '@ghostscreen/contracts';
import { QueryErrorState } from '../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';
import { api } from '../../lib/api';
import { signOut } from '../../lib/auth/signOut';
import { useSignedInEmail } from '../../lib/auth/useSignedInEmail';
import { useQueryRetry } from '../../lib/dashboard/useQueryRetry';
import { setSelectedOrganisationId } from '../../lib/session';
import { CreateOrganisationDialog } from './CreateOrganisationDialog';
import { OrganisationList } from './OrganisationList';
import { OrganisationShowcase } from './OrganisationShowcase';
import { useListboxNavigation } from './useListboxNavigation';
import './chooseOrganisation.css';

const NO_ORGANISATIONS: OrganisationMembership[] = [];

/** Post-login business picker (route `/setup`). */
export function ChooseOrganisationPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const email = useSignedInEmail();
  const [creating, setCreating] = useState(false);
  const createButtonRef = useRef<HTMLButtonElement>(null);

  const organisationsQuery = useQuery({
    queryKey: ['my-organisations'],
    queryFn: api.listMyOrganisations,
  });
  const { isRetrying, retry } = useQueryRetry(() => organisationsQuery.refetch());
  const organisations = organisationsQuery.data ?? NO_ORGANISATIONS;

  const openOrganisation = useCallback(
    (organisationId: string) => {
      setSelectedOrganisationId(organisationId);
      navigate('/', { replace: true });
    },
    [navigate],
  );

  const openByIndex = useCallback(
    (index: number) => {
      const organisation = organisations[index];
      if (organisation) openOrganisation(organisation.id);
    },
    [openOrganisation, organisations],
  );

  const listbox = useListboxNavigation({
    count: organisations.length,
    enabled: !creating,
    onOpen: openByIndex,
  });

  const closeCreateDialog = useCallback(() => {
    setCreating(false);
    createButtonRef.current?.focus();
  }, []);

  const leave = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const previewIndex = listbox.previewIndex < organisations.length ? listbox.previewIndex : 0;

  return (
    <div className="org-picker">
      <OrganisationShowcase
        organisation={organisations[previewIndex] ?? null}
        organisationIndex={previewIndex}
      />

      <section className="org-chooser">
        <nav className="org-chooser__account" aria-label={t('chooseOrganisation.accountNav')}>
          <button type="button" className="org-link" onClick={() => void leave()}>
            {t('chooseOrganisation.switchAccount')}
          </button>
          <button type="button" className="org-link" onClick={() => void leave()}>
            {t('common:actions.signOut')}
          </button>
        </nav>

        <main className="org-chooser__body">
          <div className="org-chooser__heading">
            <h1>{t('chooseOrganisation.title')}</h1>
            {email ? (
              <p>
                <Trans
                  t={t}
                  i18nKey="chooseOrganisation.signedInAs"
                  values={{ email }}
                  components={{ email: <span dir="ltr" /> }}
                />
              </p>
            ) : null}
          </div>

          {organisationsQuery.isError || isRetrying ? (
            <QueryErrorState
              message={t('chooseOrganisation.loadError')}
              onRetry={retry}
              isRetrying={isRetrying}
            />
          ) : organisationsQuery.isPending ? (
            <QueryLoadingBlock variant="rows" />
          ) : (
            <OrganisationList
              organisations={organisations}
              activeIndex={listbox.activeIndex}
              registerOption={listbox.registerOption}
              createButtonRef={createButtonRef}
              onHighlight={listbox.highlight}
              onOpen={(organisation) => openOrganisation(organisation.id)}
              onCreate={() => setCreating(true)}
            />
          )}

          {organisations.length > 0 ? (
            <p className="org-hint">
              <Trans
                t={t}
                i18nKey="chooseOrganisation.keyboardHint"
                components={{ kbd: <kbd className="org-kbd" /> }}
              />
            </p>
          ) : null}
        </main>
      </section>

      {creating ? (
        <CreateOrganisationDialog
          onClose={closeCreateDialog}
          onCreated={(organisation) => openOrganisation(organisation.id)}
        />
      ) : null}
    </div>
  );
}
